import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Search, X, Phone } from "lucide-react";

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "report", label: "Report response" },
  { value: "feedback", label: "Feedback response" },
  { value: "promotion", label: "Promotion" },
  { value: "safety", label: "Safety" },
];

export const AdminMessagePanel = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [target, setTarget] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const d = await api.adminSearchUsers(q.trim());
      setResults(d.users || []);
    } catch {
      toast.error("Search failed.");
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!target) return toast.error("Pick a member first.");
    if (!subject.trim()) return toast.error("Subject is required.");
    if (!message.trim()) return toast.error("Message is required.");
    setBusy(true);
    try {
      const res = await api.adminSendMessage({
        user_id: target.user_id, subject: subject.trim(), message: message.trim(),
        send_email: sendEmail, category,
      });
      toast.success(res.texted ? "Message sent to inbox + text (SMS)." : "Message sent to member's inbox.");
      setSubject(""); setMessage(""); setTarget(null); setResults(null); setQ("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the message.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-10 rounded-2xl border border-[#10B981]/30 bg-white/70 backdrop-blur-lg shadow-sm p-6" data-testid="admin-message-panel">
      <div className="flex items-center gap-2 text-[#1A1A1A]">
        <Send className="w-4 h-4 text-[#10B981]" />
        <h2 className="font-heading text-2xl">Message a member</h2>
      </div>
      <p className="text-lg text-[#5A5A5A] mt-1">Reach out about reports, feedback, promotions, or anything else. Lands in their Welp inbox — and straight to their verified phone by text.</p>

      {!target && (
        <div className="mt-5">
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Search by email or name…"
              className="flex-1 rounded-full border border-black/15 px-5 py-2.5 text-sm bg-white/70 backdrop-blur-lg text-[#1A1A1A] focus:outline-none focus:border-[#10B981]"
              data-testid="admin-msg-search-input" />
            <button onClick={search} disabled={busy}
              className="px-5 py-2.5 rounded-full bg-black/10 text-white text-sm font-semibold hover:brightness-125 transition inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="admin-msg-search-btn">
              <Search className="w-4 h-4" /> Search
            </button>
          </div>
          {results !== null && (
            <ul className="mt-4 divide-y divide-black/10 max-h-64 overflow-y-auto" data-testid="admin-msg-results">
              {results.length === 0 && <li className="py-3 text-sm text-[#5A5A5A]">No members found.</li>}
              {results.map((u) => (
                <li key={u.user_id}>
                  <button onClick={() => setTarget(u)} data-testid={`admin-msg-pick-${u.user_id}`}
                    className="w-full text-left py-3 px-2 hover:bg-[#10B981]/5 rounded-lg transition flex items-center justify-between gap-3">
                    <span>
                      <span className="text-[#1A1A1A] font-semibold text-sm">{u.display_name || "(no name)"}</span>
                      <span className="text-[#5A5A5A] text-sm ml-2">{u.email}</span>
                    </span>
                    <span className="text-xs text-[#5A5A5A]">{u.banned ? "banned" : u.auth_provider}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {target && (
        <div className="mt-5 space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-[#10B981]/5 border border-[#10B981]/20 px-4 py-3" data-testid="admin-msg-target">
            <div className="text-sm text-[#1A1A1A]"><strong>To:</strong> {target.display_name || "(no name)"} <span className="text-[#5A5A5A]">· {target.email}</span></div>
            <button onClick={() => setTarget(null)} className="text-[#5A5A5A] hover:text-[#1A1A1A]" data-testid="admin-msg-clear-target" aria-label="Clear recipient"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c.value} onClick={() => setCategory(c.value)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${category === c.value ? "bg-[#10B981] text-white border-[#10B981]" : "bg-white/70 backdrop-blur-lg text-[#5A5A5A] border-black/15 hover:border-[#10B981]/50"}`}
                data-testid={`admin-msg-cat-${c.value}`}>{c.label}</button>
            ))}
          </div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150}
            placeholder="Subject"
            className="w-full rounded-xl border border-black/15 px-4 py-2.5 text-sm bg-white/70 backdrop-blur-lg text-[#1A1A1A] focus:outline-none focus:border-[#10B981]"
            data-testid="admin-msg-subject" />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} rows={5}
            placeholder="Write your message…"
            className="w-full rounded-xl border border-black/15 px-4 py-3 text-sm bg-white/70 backdrop-blur-lg text-[#1A1A1A] focus:outline-none focus:border-[#10B981] resize-y"
            data-testid="admin-msg-body" />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-[#3A3A3A] cursor-pointer">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                className="accent-[#10B981] w-4 h-4" data-testid="admin-msg-send-email" />
              <Phone className="w-4 h-4 text-[#5A5A5A]" /> Also text them (SMS)
            </label>
            <button onClick={send} disabled={busy}
              className="px-6 py-2.5 rounded-full bg-[#10B981] text-white text-sm font-bold glow-pink hover:brightness-110 transition disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="admin-msg-send-btn">
              <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send message"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
